import type {
  AdminSponsorshipApplication,
  AdminSponsorshipApplicationPage,
} from '@inflip/contracts/admin';
import type { SponsorshipApplicationStatus } from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { DataTable } from '../components/data-table';
import {
  qualifiedChannelLabel,
  SponsorshipTitleCell,
} from '../components/sponsorship-application-cells';
import {
  Button,
  Chip,
  Dialog,
  EmptyState,
  ErrorBanner,
  LoadingRow,
  PageHeader,
  inputCls,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { SPONSORSHIP_APPLICATION_STATUS_META } from '../lib/status-ui';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';

const PAGE_SIZE = 20;

/** 표 칸 — 메시지 칸의 "확인" 버튼이 팝업을 열어야 해서 화면 안에서 만든다 */
function makeColumns(
  onShowMessage: (a: AdminSponsorshipApplication) => void,
): ColumnDef<AdminSponsorshipApplication>[] {
  return [
    {
      id: 'applicant',
      header: '지원자',
      size: 170,
      // 지원자 → 그 회원 상세의 협찬 지원 탭
      cell: ({ row }) => (
        <Link
          to="/users/$userId"
          params={{ userId: row.original.applicantId }}
          search={{ tab: 'sponsorships' }}
          className="group block min-w-0"
        >
          <div className="truncate font-semibold group-hover:text-brand group-hover:underline">
            {row.original.applicantName}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {row.original.applicantEmail}
          </div>
        </Link>
      ),
    },
    {
      id: 'sponsorship',
      header: '협찬',
      size: 220,
      // 협찬 → 협찬 심사에서 그 제목으로 검색 (상태 전체)
      cell: ({ row }) => (
        <Link
          to="/sponsorships"
          search={{ status: '', q: row.original.sponsorshipTitle }}
          className="group block min-w-0"
        >
          <SponsorshipTitleCell a={row.original} linked />
        </Link>
      ),
    },
    {
      id: 'owner',
      header: '협찬 작성자',
      size: 170,
      // 협찬을 등록한 회원 → 그 회원 상세
      cell: ({ row }) => (
        <Link
          to="/users/$userId"
          params={{ userId: row.original.ownerId }}
          className="group block min-w-0"
        >
          <div className="truncate font-semibold group-hover:text-brand group-hover:underline">
            {row.original.ownerName}
          </div>
          <div className="truncate text-xs text-muted-foreground">{row.original.ownerEmail}</div>
        </Link>
      ),
    },
    {
      id: 'status',
      header: '상태',
      size: 80,
      cell: ({ row }) => {
        const meta = SPONSORSHIP_APPLICATION_STATUS_META[row.original.status];
        return (
          <>
            <span className={`text-xs font-bold ${meta.cls}`}>{meta.label}</span>
            {/* 거절 사유 — 칸이 좁아 한 줄로 자르고, 전체는 마우스를 올려 본다 */}
            {row.original.rejectReason && (
              <span
                title={row.original.rejectReason}
                className="block truncate text-[11px] text-muted-foreground"
              >
                {row.original.rejectReason}
              </span>
            )}
          </>
        );
      },
    },
    {
      id: 'channel',
      header: '지원 채널',
      size: 120,
      cell: ({ row }) => qualifiedChannelLabel(row.original),
      meta: { tdClass: 'text-xs text-muted-foreground' },
    },
    {
      id: 'message',
      header: '메시지',
      size: 70,
      // 지원할 때 메시지를 남긴 경우에만 버튼이 있다.
      cell: ({ row }) =>
        row.original.message ? (
          <Button variant="secondary" size="sm" onClick={() => onShowMessage(row.original)}>
            확인
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      id: 'visitDate',
      header: '방문 예정일',
      size: 100,
      cell: ({ row }) => row.original.visitDate ?? '—',
      meta: { tdClass: 'text-xs text-muted-foreground' },
    },
    {
      id: 'createdAt',
      header: '지원일',
      cell: ({ row }) => formatDateTime(row.original.createdAt),
      meta: { tdClass: 'text-xs text-muted-foreground' },
    },
    {
      id: 'decidedAt',
      header: '승인·거절일',
      cell: ({ row }) => (row.original.decidedAt ? formatDateTime(row.original.decidedAt) : '—'),
      meta: { tdClass: 'text-xs text-muted-foreground' },
    },
  ];
}

/**
 * 협찬 지원 내역 — 모든 회원의 협찬 지원을 최근 지원순으로.
 * 지원자를 누르면 회원 상세(협찬 지원 탭), 협찬을 누르면 협찬 심사에서 그 제목으로 검색, 작성자를 누르면 그 회원 상세
 */
export function SponsorshipApplicationsPage() {
  const { session } = useAdminAuth();
  // 목록 상태는 URL 에 — 회원 상세에서 뒤로 나와도 보던 페이지·필터가 유지된다
  const [status, setStatus] = useListViewState<SponsorshipApplicationStatus | ''>('status', '');
  const [searchTerm, setSearchTerm] = useListViewState('q', '');
  const [q, setQ] = useState(searchTerm);
  const [page, setPage] = useListViewState('page', 1);
  const [result, setResult] = useState<AdminSponsorshipApplicationPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [messageOf, setMessageOf] = useState<AdminSponsorshipApplication | null>(null);
  const columns = useMemo(() => makeColumns(setMessageOf), []);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setError(null);
    api
      .listSponsorshipApplications(session.accessToken, {
        page,
        pageSize: PAGE_SIZE,
        ...(status ? { status } : {}),
        ...(searchTerm ? { q: searchTerm } : {}),
      })
      .then((res) => {
        if (!cancelled) setResult(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [session, status, searchTerm, page]);

  if (!session) return null;
  const rows = result?.items ?? [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="협찬 지원 내역"
        description={
          result
            ? `${status ? SPONSORSHIP_APPLICATION_STATUS_META[status].label : '전체'} ${result.total.toLocaleString('ko-KR')}건`
            : '회원들의 협찬 지원을 모아 봐요'
        }
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearchTerm(q.trim());
            }}
          >
            <input
              className={`w-72 ${inputCls}`}
              placeholder="지원자·작성자 이름·이메일 / 협찬 제목·업체명"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit">검색</Button>
          </form>
        }
      />

      <div className="mb-4 flex flex-wrap gap-1.5">
        <Chip
          active={status === ''}
          onClick={() => {
            setPage(1);
            setStatus('');
          }}
        >
          전체
        </Chip>
        {Object.entries(SPONSORSHIP_APPLICATION_STATUS_META).map(([key, meta]) => (
          <Chip
            key={key}
            active={status === key}
            onClick={() => {
              setPage(1);
              setStatus(key as SponsorshipApplicationStatus);
            }}
          >
            {meta.label}
          </Chip>
        ))}
      </div>

      {error && <ErrorBanner message={error} />}
      {!result && !error && <LoadingRow />}

      {result && rows.length === 0 ? (
        <EmptyState message="해당하는 협찬 지원이 없어요." />
      ) : (
        <DataTable data={rows} columns={columns} getRowId={(a) => a.id} />
      )}

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {result.page} / {result.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= result.totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}

      {messageOf && (
        <Dialog label="지원 메시지" onClose={() => setMessageOf(null)}>
          <p className="text-lg font-bold tracking-tight">지원 메시지</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {messageOf.applicantName} · {messageOf.sponsorshipTitle}
          </p>
          <p className="mt-4 max-h-[50vh] overflow-y-auto whitespace-pre-wrap rounded-md bg-muted px-3.5 py-3 text-sm leading-relaxed">
            {messageOf.message}
          </p>
          <Button variant="secondary" className="mt-5 w-full" onClick={() => setMessageOf(null)}>
            닫기
          </Button>
        </Dialog>
      )}
    </main>
  );
}
